//
// Copyright 2025 DXOS.org
//

import { RegistryContext } from '@effect/atom-react/RegistryContext';
import * as Effect from 'effect/Effect';
import * as Fiber from 'effect/Fiber';
import * as PubSub from 'effect/PubSub';
import React, { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as EffectEx from '@dxos/effect/EffectEx';
import { BaseError, withContext } from '@dxos/errors';
import { invariant } from '@dxos/invariant';
import { log } from '@dxos/log';
import { ErrorBoundary, ErrorFallback, type FallbackProps } from '@dxos/react-error-boundary';
import { useAsyncEffect, useDefaultValue } from '@dxos/react-hooks';
import { ContextProtocolProvider } from '@dxos/web-context-react';

import { ActivationEvents, Capabilities } from '../../common/index.ts';
import { PluginManagerContext } from '../../context.ts';
import { type ActivationEvent, type Plugin, PluginManager } from '../../core/index.ts';
import * as Devtools from '../../Devtools.ts';
import { App } from '../components/App/App.tsx';
import { bootLoader } from '../components/App/loader.ts';
import { PluginManagerProvider } from '../components/PluginManager/PluginManagerProvider.ts';
import { SurfaceManager } from '../components/Surface/SurfaceManager.ts';
import { SurfaceManagerProvider } from '../components/Surface/SurfaceManagerContext.ts';
import { createStartupWatchdog } from './startup-watchdog.ts';

const ENABLED_KEY = 'org.dxos.app-framework.enabled';

export type StartupProgress = {
  /** Number of modules that have been activated. */
  activated: number;
  /** Total number of modules registered. */
  total: number;
  /** Fractional progress (0-1). */
  progress: number;
  /**
   * Raw activation event key (e.g. `org.dxos.app-framework.event.startup`).
   * Set on event-level transitions, *and* on module-level transitions where
   * it carries the parent activation event that first triggered the
   * module's load (plumbed through `_loadCapabilitiesForModules` →
   * `_loadModule`). Consumers can use this either as the primary id (when
   * {@link module} is absent) or as an extra "context" field alongside
   * {@link module}.
   */
  event?: string;
  /**
   * Raw module id (e.g. `org.dxos.plugin.observability.module.ReactSurface`)
   * when the in-flight activation is module-level. When present,
   * {@link event} may also be set, identifying the parent activation that
   * triggered this module's load.
   */
  module?: string;
  /**
   * Pre-humanized label for the currently surfaced transition (module
   * label if {@link module} is set, otherwise the event label), supplied
   * for consumers that want a sensible default. Hosts that prefer to
   * render their own label can read the raw {@link event}/{@link module}
   * fields and ignore this — the framework leaves the policy choice
   * (which transitions to surface, how to format them, whether to drop
   * sub-modules entirely) to the host's `Placeholder`.
   */
  humanizedName?: string;
  /**
   * Humanized label of the plugin owning {@link module} (e.g. "Markdown"), absent on
   * event-level transitions. Lets a host collapse a plugin's many module activations
   * into a single status line without re-parsing module ids.
   */
  pluginName?: string;
  /** Slug of the plugin owning {@link module} (e.g. `markdown`) — the id the boot loader's row keys on. */
  pluginSlug?: string;
};

/**
 * Event dispatched on `window` once the `Startup` activation event has fully completed.
 */
export const STARTUP_ACTIVATED_EVENT = 'app-framework:startup-activated';

/**
 * Event dispatched on `window` when startup misses its deadline, carrying
 * {@link StartupDiagnostics} as `detail`.
 */
export const STARTUP_FAILED_EVENT = 'app-framework:startup-failed';

/**
 * Event dispatched on `window` the first time the app shell renders and the boot loader is
 * dismissed, carrying the milliseconds since navigation start as `detail`.
 */
export const FIRST_INTERACTIVE_EVENT = 'app-framework:first-interactive';

/** Startup did not complete before its deadline. Carries {@link StartupDiagnostics} as context. */
export class StartupTimeoutError extends BaseError.extend('StartupTimeoutError', 'Startup timed out') {}

/** Where startup had got to when the deadline expired. */
export type StartupDiagnostics = {
  /** Which failure produced these: the deadline expiring, or a module refusing to activate. */
  startupFailureKind: 'timeout' | 'module-error';
  /** The deadline, in ms; reported for both kinds so the two are comparable. */
  startupTimeoutMs: number;
  /** Comma-separated activation events that completed before the failure. */
  startupEventsFired: string;
  /** Modules that had finished activating. */
  startupActivatedModules: number;
  /** Modules registered in total. */
  startupTotalModules: number;
  /** Comma-separated modules still activating, which is the set the stall is in. */
  startupInFlightModules: string;
  /** Activation event in flight when the failure hit. */
  startupLastEvent?: string;
  /** Comma-separated events queued for replay by a pending reset. */
  startupPendingReset: string;
};

declare global {
  interface WindowEventMap {
    [STARTUP_ACTIVATED_EVENT]: CustomEvent<void>;
    [STARTUP_FAILED_EVENT]: CustomEvent<StartupDiagnostics>;
    [FIRST_INTERACTIVE_EVENT]: CustomEvent<number>;
  }
}

export type UseAppOptions = {
  pluginManager?: PluginManager.PluginManager;
  pluginLoader?: PluginManager.ManagerOptions['pluginLoader'];
  onPluginRemove?: PluginManager.ManagerOptions['onRemove'];
  pluginRegistryProvider?: PluginManager.ManagerOptions['pluginRegistryProvider'];
  plugins?: Plugin.Plugin[];
  defaults?: string[];
  /**
   * Additional activation events to fire before startup.
   */
  setupEvents?: ActivationEvent.ActivationEvent[];
  cacheEnabled?: boolean;
  safeMode?: boolean;
  debounce?: number;
  /**
   * Executed milliseconds without an activation event before startup is declared stalled; suspended
   * time does not count, and a boot that keeps activating is never aborted.
   */
  timeout?: number;
  /**
   * Relay per-plugin `Activating …` lines into the boot loader's status log. Off by default —
   * activation names framework internals, which is diagnostic detail rather than something a
   * user booting the app is asking for.
   */
  verboseStatus?: boolean;
  fallback?: FC<FallbackProps>;
};

/**
 * Expected usage is for this to be the entrypoint of the application.
 * Initializes plugins and renders the root components.
 *
 * @example
 * const plugins = [LayoutPlugin(), MyPlugin()];
 * const defaults = [MyPluginId];
 * const fallback = () => <div>Initializing Plugins...</div>;
 * const App = useApp({ plugins, defaults, fallback });
 * createRoot(document.getElementById('root')!).render(
 *   <StrictMode>
 *     <App />
 *   </StrictMode>,
 * );
 *
 * @param params.pluginLoader A function which loads new plugins.
 * @param params.plugins All plugins available to the application. Plugins whose `meta.profile.tags` includes `'system'` are treated as core (force-enabled, not user-toggleable).
 * @param params.defaults Default plugins are enabled by default but can be disabled by the user.
 * @param params.cacheEnabled Whether to cache enabled plugins in localStorage.
 * @param params.safeMode Whether to enable safe mode, which disables optional plugins.
 * @param params.fallback Fallback component to render if an error occurs during startup.
 */
export const useApp = ({
  pluginManager,
  pluginLoader: pluginLoaderProp,
  onPluginRemove,
  pluginRegistryProvider,
  plugins: pluginsProp,
  defaults: defaultsProp,
  setupEvents: setupEventsProp,
  fallback = ErrorFallback,
  cacheEnabled = false,
  safeMode = false,
  debounce = 0,
  timeout = 30_000,
  verboseStatus = false,
}: UseAppOptions) => {
  const plugins = useDefaultValue(pluginsProp, () => []);
  const defaults = useDefaultValue(defaultsProp, () => []);
  const setupEvents = useDefaultValue(setupEventsProp, () => []);

  const pluginLoader = useMemo(
    () =>
      pluginLoaderProp ??
      ((id: string) =>
        Effect.sync(() => {
          const plugin = plugins.find((plugin) => plugin.meta.profile.key === id);
          invariant(plugin, `Plugin not found: ${id}`);
          return { plugin };
        })),
    [pluginLoaderProp, plugins],
  );

  const readyRef = useRef(false);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [startupProgress, setStartupProgress] = useState<StartupProgress>({
    activated: 0,
    total: 0,
    progress: 0,
  });
  // TODO(wittjosiah): Migrate to Atom.kvs for isomorphic storage.
  const cached: string[] = useMemo(() => JSON.parse(localStorage.getItem(ENABLED_KEY) ?? '[]'), []);
  const enabled = useMemo(
    () => (safeMode ? [] : cacheEnabled && cached.length > 0 ? cached : defaults),
    [safeMode, cacheEnabled, cached, defaults],
  );
  const isExternalManager = !!pluginManager;
  const manager = useMemo(() => {
    const mgr =
      pluginManager ??
      PluginManager.make({
        pluginLoader,
        plugins,
        enabled,
        onRemove: onPluginRemove,
        pluginRegistryProvider,
      });
    log('useApp: useMemo created/reused manager', { provided: !!pluginManager });
    return mgr;
  }, [pluginManager, pluginLoader, plugins, enabled, onPluginRemove, pluginRegistryProvider]);

  useEffect(() => {
    if (!cacheEnabled) {
      return;
    }
    return manager.registry.subscribe(manager.enabled, (value) => {
      localStorage.setItem(ENABLED_KEY, JSON.stringify(value));
    });
  }, [cacheEnabled, manager]);

  useEffect(() => {
    Devtools.setup(manager);
  }, [manager]);

  // Hand the boot loader the enabled plugins' icons from their own meta. This registers, it does
  // not draw: barely half the enabled set activates during startup (the rest activate lazily on
  // first use), so a row seeded from it sat half-dim for the whole boot. Meta is the icon's only
  // source — the loader is a standalone bundle with no access to the plugin registry.
  useEffect(() => {
    // Optional call, not just an optional facade: an already-loaded page can be running an
    // `index.html` whose inlined loader bundle predates this method (service-worker cache, stale
    // dev compile), and a TypeError here would strand that page on the loader forever.
    bootLoader?.plugins?.(
      plugins
        .filter(({ meta }) => enabled.includes(meta.profile.key))
        .map(({ meta }) => ({
          id: pluginSlugOfKey(meta.profile.key),
          icon: meta.profile.icon?.key,
        })),
    );
  }, [plugins, enabled]);

  useAsyncEffect(async () => {
    log('useApp: effect mount');

    manager.capabilities.contribute({
      interface: Capabilities.PluginManager,
      implementation: manager,
      module: 'org.dxos.app-framework.plugin-manager',
    });

    manager.capabilities.contribute({
      interface: Capabilities.AtomRegistry,
      implementation: manager.registry,
      module: 'org.dxos.app-framework.atom-registry',
    });

    const inFlightModules = new Set<string>();

    const collectDiagnostics = (startupFailureKind: StartupDiagnostics['startupFailureKind']): StartupDiagnostics => ({
      startupFailureKind,
      startupTimeoutMs: timeout,
      startupEventsFired: manager.getEventsFired().join(','),
      startupActivatedModules: manager.getActive().length,
      startupTotalModules: manager.getModules().length,
      startupInFlightModules: [...inFlightModules].join(','),
      startupLastEvent: progressRef.current.event,
      startupPendingReset: manager.getPendingReset().join(','),
    });

    const reportFailure = (diagnostics: StartupDiagnostics) => {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(STARTUP_FAILED_EVENT, { detail: diagnostics }));
      }
    };

    const watchdog = createStartupWatchdog({
      stallMs: timeout,
      onStall: ({ executedMs }) => {
        const diagnostics = collectDiagnostics('timeout');

        log.warn('startup timeout diagnostic', { ...diagnostics, executedMs, activeModules: manager.getActive() });

        const abort = () => {
          void EffectEx.runAndForwardErrors(Fiber.interrupt(fiber));
          setError(
            withContext(new StartupTimeoutError({ message: `Startup timed out after ${timeout}ms` }), diagnostics),
          );
          reportFailure(diagnostics);
        };

        // In development the deadline is a symptom, not a verdict: a cold OPFS, a rebuild or a paused
        // debugger all overrun it while the run is perfectly healthy, and killing it discards the
        // state worth looking at. Startup continues either way and the user decides — the offer raises
        // exactly the failure this branch used to raise unprompted.
        //
        // The missing-`stalled` case does NOT fall through to failing: the loader is inlined into
        // `index.html` at build time, so a page served before this shipped has the old bundle, and
        // treating that as fatal would resurrect the dialog precisely where dev asked for a button.
        if (import.meta.env?.DEV) {
          if (bootLoader?.stalled) {
            bootLoader.stalled(abort);
          } else {
            log.warn('startup timed out; boot loader cannot offer an abort (stale inlined bundle?)', { timeout });
          }
          return;
        }

        abort();
      },
    });

    const fiber = Effect.gen(function* () {
      const queue = yield* PubSub.subscribe(manager.activation);
      const listener = yield* Effect.forkDetach(
        PubSub.take(queue).pipe(
          Effect.tap(({ event, state, module, error: error$ }) =>
            Effect.sync(() => {
              watchdog.progress();
              if (module) {
                if (state === 'activating') {
                  inFlightModules.add(module);
                } else {
                  inFlightModules.delete(module);
                }
              }
              // Event-level Startup activated (no `module` field) fires once,
              // after every module triggered by Startup has finished. Module
              // activations now also carry their parent event id (so the trace
              // can attribute a module to its triggering event), which means
              // each module activated under Startup publishes
              // `{ event: 'startup', state: 'activated', module: <id> }`. Without
              // the `!module` guard the listener would mark the app ready on
              // the *first* such module rather than waiting for the event-level
              // completion — leaving downstream capabilities (operation-invoker,
              // app-graph, …) un-registered when the boot loader dismisses.
              if (event === ActivationEvents.Startup.id && state === 'activated' && !module) {
                watchdog.dispose();
                setReady(true);
                readyRef.current = true;
                // Trigger startup profiler dump if available.
                (globalThis as any).composer?.profiler?.dump();
                // Notify any host observability layer that startup completed.
                // A `CustomEvent` keeps this generic — app-framework doesn't
                // import a provider, and consumers can capture the startup
                // summary without us picking one.
                if (typeof window !== 'undefined') {
                  window.dispatchEvent(new CustomEvent(STARTUP_ACTIVATED_EVENT));
                }
                return;
              }
              // `activating` is the start-of-load signal. Surface the raw
              // `module` (or `event`) plus a pre-humanized label so the
              // boot loader can decide how to render each transition — show
              // everything, suppress noisy sub-modules, group by plugin,
              // or apply its own formatting. We intentionally do NOT touch
              // these fields on `activated`: pairing the two would cause
              // back-to-back identical updates to the host's effect, which
              // the boot loader treats as a re-trigger because
              // `progress.progress` moved. Leaving the label alone on
              // completion keeps it accurate ("now activating X") until
              // the next module starts.
              if (module && state === 'activating' && !readyRef.current) {
                const pluginSlug = pluginSlugOf(module);
                setStartupProgress((current) => ({
                  ...current,
                  // `event` here is the activation event that first
                  // triggered this module load (plumbed through
                  // `_loadCapabilitiesForModules` → `_loadModule`).
                  // Falsy/empty falls back to undefined so consumers can
                  // tell "no parent context" from "parent context: <X>".
                  event: event || undefined,
                  module,
                  humanizedName: humanizeModuleId(module),
                  pluginName: pluginSlug ? titleCase(pluginSlug) : undefined,
                  pluginSlug,
                }));
              }
              // Update the activation count when a module commits. The
              // ring's fraction comes from this; `event`/`module`/
              // `humanizedName` were set by the matching `activating`
              // message above and are left alone so the count can advance
              // without re-firing the host's status callback.
              if (module && state === 'activated' && !readyRef.current) {
                const active = manager.getActive();
                const total = manager.getModules().length;
                setStartupProgress((current) => ({
                  ...current,
                  activated: active.length,
                  total,
                  progress: total > 0 ? active.length / total : 0,
                }));
              }
              // Event-level `activating` (no `module`) — fired at the start
              // of `_activateModulesForEvent` and recursively for each
              // before/after event. Surfaces a label during the gap before
              // the first module-level message lands; subsequent module
              // updates immediately overwrite this with a more specific
              // label.
              if (event && !module && state === 'activating' && !readyRef.current) {
                setStartupProgress((current) => ({
                  ...current,
                  event,
                  module: undefined,
                  humanizedName: humanizeEventKey(event),
                  pluginName: undefined,
                  pluginSlug: undefined,
                }));
              }
              if (error$ && !readyRef.current) {
                watchdog.dispose();
                const diagnostics = collectDiagnostics('module-error');
                setError(withContext(error$, diagnostics));
                reportFailure(diagnostics);
              }
            }),
          ),
          Effect.forever,
        ),
      );

      yield* Effect.all([
        ...setupEvents.map((event) => manager.activate(event)),
        manager.activate(ActivationEvents.Startup),
      ]);

      return yield* Fiber.join(listener);
    }).pipe(Effect.scoped, Effect.runFork);

    return () => {
      log('useApp: effect cleanup');
      watchdog.dispose();
      void EffectEx.runAndForwardErrors(Fiber.interrupt(fiber));
      if (!isExternalManager) {
        EffectEx.runDetached(manager.shutdown());
      }
    };
  }, [manager]);

  const progressRef = useRef(startupProgress);
  progressRef.current = startupProgress;

  const surfaces = useMemo(() => new SurfaceManager(manager.capabilities, manager), [manager]);

  return useCallback(
    () => (
      <ErrorBoundary name='app' FallbackComponent={fallback}>
        <PluginManagerProvider value={manager}>
          <ContextProtocolProvider value={manager} context={PluginManagerContext}>
            <RegistryContext.Provider value={manager.registry}>
              <SurfaceManagerProvider value={surfaces}>
                <App
                  ready={ready}
                  error={error}
                  debounce={debounce}
                  progress={progressRef.current}
                  verboseStatus={verboseStatus}
                />
              </SurfaceManagerProvider>
            </RegistryContext.Provider>
          </ContextProtocolProvider>
        </PluginManagerProvider>
      </ErrorBoundary>
    ),
    [fallback, manager, surfaces, ready, error],
  );
};

/**
 * Extracts a human-readable label from a module ID.
 *
 * Module IDs follow `org.dxos.plugin.<plugin-slug>.module.<module-name>`,
 * where `<plugin-slug>` is kebab-case and `<module-name>` is either an
 * explicit string (often kebab-case, e.g. `'observability'`, `'namespace'`)
 * or a capability tag in PascalCase from `Capability.getModuleTag(...)`
 * (e.g. `'ReactSurface'`, `'AppGraphBuilder'`). The output is
 * `"Title Case Plugin: kebab-module"` so the visible status names both the
 * plugin and the aspect being activated, helping disambiguate the multiple
 * modules a plugin contributes.
 *
 * Examples:
 *   - "org.dxos.plugin.markdown.module.ReactSurface" → "Markdown: react-surface"
 *   - "org.dxos.plugin.observability.module.AppGraphBuilder" → "Observability: app-graph-builder"
 *   - "org.dxos.plugin.observability.module.observability" → "Observability"
 *     (the module name matches the plugin slug — collapsed to avoid
 *     "Observability: observability" noise.)
 */
const humanizeModuleId = (moduleId: string): string => {
  const match = moduleId.match(/\.plugin\.([^.]+)\.module\.(.+)$/);
  if (!match) {
    // Fallback: use the last segment.
    const parts = moduleId.split('.');
    return parts[parts.length - 1];
  }
  const [, pluginSlug, moduleName] = match;
  const pluginLabel = titleCase(pluginSlug);
  // Normalise the module name to kebab-case so PascalCase capability tags
  // ("ReactSurface") read consistently with explicit kebab IDs
  // ("operation-handler"). The two-step substitution handles consecutive
  // uppercase runs (`URLLoader` → `url-loader`) without splitting them
  // mid-acronym.
  const moduleLabel = moduleName
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1-$2')
    .toLowerCase();
  // Capability modules whose name matches the plugin slug (e.g. the
  // observability plugin's `observability` capability module) would render
  // as "Observability: observability" — drop the redundant suffix.
  if (moduleLabel === pluginSlug) {
    return pluginLabel;
  }
  return `${pluginLabel}: ${moduleLabel}`;
};

/**
 * Extracts the owning plugin's slug from a module ID, or `undefined` when the id is not
 * plugin-scoped (framework-internal modules). E.g.
 * "org.dxos.plugin.markdown.module.ReactSurface" → "markdown".
 */
/** Plugin slug from a plugin key — "org.dxos.plugin.markdown" → "markdown". */
export const pluginSlugOfKey = (key: string): string => key.split('.').at(-1) ?? key;

export const pluginSlugOf = (moduleId: string): string | undefined =>
  moduleId.match(/\.plugin\.([^.]+)\.module\./)?.[1];

/** Kebab slug → Title Case. */
const titleCase = (slug: string): string =>
  slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Extracts a human-readable label from an activation event key.
 * E.g., "org.dxos.app-framework.event.setup-react-surface" → "Setup React Surface".
 */
const humanizeEventKey = (eventKey: string): string => {
  // Strip a leading specifier (composite key form: "<id>:<specifier>").
  const id = eventKey.split(':')[0];
  // Match the trailing segment after `.event.`.
  const match = id.match(/\.event\.(.+)$/);
  const slug = match ? match[1] : (id.split('.').pop() ?? id);
  return titleCase(slug);
};
